import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import CreateEditContact from './CreateEditContact'
import i18n from '~/components/i18n'
import TableComponent from '~/components/TableComponent'
import { useAuth } from '~/routes/components/AuthProvider'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { FarmersService } from '~/api/services/farmers'
import notify from '~/components/Notify'
import { handleErrors } from '~/utils/helpers'
import Dialog from '~/components/Dialog'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('First name'),
    key: 'firstName'
  },
  {
    name: i18n.t('Last name'),
    key: 'lastName'
  },
  {
    name: i18n.t('Position'),
    key: 'position'
  },
  {
    name: i18n.t('Mobile number'),
    key: 'mobileNumber'
  },
  {
    name: i18n.t('Phone number'),
    key: 'phoneNumber'
  },
  {
    name: i18n.t('Email'),
    key: 'email'
  },
  {
    name: i18n.t('Actions')
  }
]

const getActions = () => [
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit',
    disableForRoleSlug: LIGHT_USER_ROLE_SLUG
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    disableForRoleSlug: LIGHT_USER_ROLE_SLUG
  }
]

const Contacts: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { logged: { roleSlug } } } = useAuth()

  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const [dataFiltered, setDataFiltered] = useState({
    rows: 10,
    page: 0
  })
  const [isBusy, setIsBusy] = useState(false)
  const [contactId, setContactId] = useState(null)
  const [deleteContactData, setDeleteContactData] = useState(null)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())

  const {
    data: contacts,
    isLoading: isLoading,
    isFetching: isFetchingData,
    refetch: fetchContacts
  } = FarmersQuery.getFarmerContacts(id, dataFiltered)

  useEffect(() => {
    fetchContacts()
  }, [dataFiltered])

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  function handleActionClick(row, type) {
    const { id } = row
    switch (type) {
      case 'edit':
        return setContactId(id)
      case 'delete':
        return setDeleteContactData(row)
      default:
        return
    }
  }

  async function handleDeleteContact() {
    setIsBusy(true);
    try {
      await FarmersService.deleteContact(id, deleteContactData?.id);
      notify(t('Successfully deleted contact'), 'success');
      setDeleteContactData(null);
      fetchContacts();
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderDeleteModal() {
    return <Dialog
      isDialogOpen={Boolean(deleteContactData)}
      textClose={t('No')}
      isLoading={isBusy}
      handleClose={() => setDeleteContactData(null)}
      textSubmit={t('Yes')}
      handleSubmit={() => handleDeleteContact()}
      textContent={t('Are you sure you want to delete contact: {{fullName}}',
        { fullName: `${deleteContactData?.firstName} ${deleteContactData?.lastName}` })}
      textHeader={t('Delete contact')}
      type={'abort'}
    />
  }

  function renderContacts() {
    const actions = getActions()
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      isFetchingData={isFetchingData}
      animated
      rows={rows}
      actions={lightUserLogged ? actions.filter(action => !action.disableForRoleSlug) : actions}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      total={filtered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
    />
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      onClick={() => setContactId('create')}
    >
      {t('Add new contact')}
    </El.PageHeaderActionButton>
  }

  const { filtered, rows } = contacts ? contacts : { filtered: 0, rows: [] }

  return <Layout
    title={''}
    useSpinner
    isBusy={isLoading}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    actions={renderActions()}
  >
    {renderDeleteModal()}
    {renderContacts()}
    <CreateEditContact
      contactId={contactId}
      handleHiddenDrawer={(submited) => {
        if (submited) {
          fetchContacts()
        }
        setContactId(null)
      }}
    />
  </Layout>
}
export default Contacts